import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { GitBranch, Loader2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api/axios'
import { formatMinutesToDHM } from '@/features/objects/utils/formatTime'
import { useCan } from '@/hooks/useCan'

interface OptionBranch {
    name: string
    duration: number
    isDefault: boolean
    skipped: boolean
    tasks: { taskNo: number; task: string; status: string; mode: string; method: string }[]
}

interface OptionGroup {
    objectId: string
    group: string
    branches: OptionBranch[]
    chosen: string | null
    decided: boolean
}

/**
 * The decisions a live recovery still holds.
 *
 * Switchable at any point, not only before the recovery starts. The reason both paths are
 * in the runbook at all is that the automated one can fail at 02:00 and the engineer needs
 * the manual one — a choice that could only be made up front would not deliver that.
 *
 * Renders nothing when a recovery has no decisions in it, which is most of them.
 */
export function RecoveryOptionsPanel({ requestID, readOnly = false }: { requestID: number | string; readOnly?: boolean }) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    // Choosing an option is managing the recovery (recover-requests.update); the choices are
    // shown to everyone who can see the runbook.
    const canChoose = useCan('recover-requests.update')

    const { data } = useQuery<{ groups: OptionGroup[] }>({
        queryKey: ['recovery-options', String(requestID)],
        queryFn: async () => (await api.get(`/api/recover-requests/${requestID}/recovery-runbook/options`)).data?.data,
        enabled: requestID != null && requestID !== '',
    })

    const choose = useMutation({
        mutationFn: (vars: { objectId: string; group: string; option: string }) =>
            api.post(`/api/recover-requests/${requestID}/recovery-runbook/options`, vars),
        onSuccess: (res, vars) => {
            queryClient.invalidateQueries({ queryKey: ['recovery-options', String(requestID)] })
            queryClient.invalidateQueries({ queryKey: ['recovery-runbook'] })
            const d = res.data?.data
            toast({
                title: `${vars.option} selected`,
                description: d?.preserved
                    ? `${d.skipped} task(s) skipped · ${d.preserved} already under way and left alone.`
                    : `${d?.skipped ?? 0} task(s) skipped.`,
            })
        },
        onError: (e: any) => toast({
            title: 'Could not change the option',
            description: e?.response?.data?.message || e?.message,
            variant: 'destructive',
        }),
    })

    const groups = data?.groups ?? []
    if (groups.length === 0) return null

    return (
        <div className="rounded-lg border-2 border-blue-300 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/10 p-3 space-y-3">
            <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-blue-800 dark:text-blue-300" aria-hidden />
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                    {t('objects.runbook.recoveryOptions', 'Recovery options')}
                </h3>
                <span className="text-xs text-blue-800/70 dark:text-blue-300/70">
                    {groups.length} {groups.length === 1
                        ? t('objects.runbook.decision', 'decision')
                        : t('objects.runbook.decisions', 'decisions')}
                </span>
            </div>

            {groups.map((g) => {
                const fastest = Math.min(...g.branches.map((b) => b.duration))
                return (
                    <div key={`${g.objectId}-${g.group}`} className="rounded-md border bg-background p-2.5">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <span className="text-sm font-medium">{g.group}</span>
                            <span className="text-xs text-muted-foreground">
                                {g.objectId}
                                {!g.decided && ` · ${t('objects.runbook.chooseOne', 'choose one')}`}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {g.branches.map((b) => {
                                const active = !b.skipped
                                const started = b.tasks.some((task) => task.status === 'Started' || task.status === 'Completed')
                                return (
                                    <Button
                                        key={b.name}
                                        type="button"
                                        size="sm"
                                        variant={active ? 'default' : 'outline'}
                                        disabled={readOnly || !canChoose || choose.isPending || (active && g.decided)}
                                        onClick={() => choose.mutate({ objectId: g.objectId, group: g.group, option: b.name })}
                                        className="h-auto py-1.5"
                                        title={started ? 'Some tasks in this branch have already started — those keep their status' : undefined}
                                    >
                                        {choose.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                                        {active && g.decided && !choose.isPending && <Check className="h-3.5 w-3.5 mr-1.5" />}
                                        <span>{b.name}</span>
                                        <span className="ml-1.5 opacity-70 text-xs">
                                            {b.tasks.length} · {formatMinutesToDHM(b.duration)}
                                            {b.duration > fastest ? ` (+${formatMinutesToDHM(b.duration - fastest)})` : ''}
                                        </span>
                                        {b.isDefault && (
                                            <span className="ml-1.5 text-[10px] uppercase opacity-60">
                                                {t('objects.runbook.plannedBranch', 'planned')}
                                            </span>
                                        )}
                                    </Button>
                                )
                            })}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
